import { useCallback, useContext, useEffect, useRef } from 'react'
import { useLongPress } from '../../../hooks/useLongPress'
import { OrbitContext } from './orbitContext'
import { orbitIsEnabled } from './orbitTypes'

const targetFor = (target: EventTarget | null) => {
  if (!(target instanceof Element) || target.closest('input, textarea, [contenteditable]')) return null
  const element = target.closest<HTMLElement>('[data-orbit-target]')
  if (!element) return null
  return element
}

const isPannableCanvasActive = (target: EventTarget | null): boolean => {
  if (!(target instanceof Element)) return false
  const pannableCanvas = target.closest<HTMLElement>('[data-pannable-canvas="true"]')
  if (!pannableCanvas) return false
  return (
    pannableCanvas.dataset.pannableCanvasPanning === 'true' ||
    pannableCanvas.dataset.pannableCanvasDragged === 'true'
  )
}

export const OrbitGestureLayer = () => {
  const orbit = useContext(OrbitContext)
  const enabled = orbitIsEnabled()
  const pressingRef = useRef(false)
  const onLongPress = useCallback(
    (event: PointerEvent) => {
      if (isPannableCanvasActive(event.target)) return
      const target = targetFor(event.target)
      if (!target) return
      orbit?.open({ x: event.clientX, y: event.clientY }, target.dataset.orbitTarget, target)
    },
    [orbit],
  )
  const press = useLongPress({
    disabled: !enabled,
    driftPx: 3,
    onLongPress,
  })

  useEffect(() => {
    if (!enabled) return

    const onContextMenu = (event: MouseEvent) => {
      if (isPannableCanvasActive(event.target)) return
      const target = targetFor(event.target)
      if (!target) return
      event.preventDefault()
      event.stopPropagation()
      orbit?.open({ x: event.clientX, y: event.clientY }, target.dataset.orbitTarget, target)
    }
    const onPointerDown = (event: PointerEvent) => {
      if (event.button === 0) {
        if (!isPannableCanvasActive(event.target)) {
          pressingRef.current = true
          press.onPointerDown(event)
        }
      }
    }
    const onPointerUp = () => {
      pressingRef.current = false
      press.onPointerUp()
    }
    const onPointerMove = (event: PointerEvent) => {
      if (!pressingRef.current) return
      if (isPannableCanvasActive(event.target)) {
        press.onPointerCancel()
        return
      }
      press.onPointerMove(event)
    }
    const onPointerCancel = () => {
      pressingRef.current = false
      press.onPointerCancel()
    }

    window.addEventListener('contextmenu', onContextMenu, true)
    window.addEventListener('pointerdown', onPointerDown, true)
    window.addEventListener('pointerup', onPointerUp, true)
    window.addEventListener('pointercancel', onPointerCancel, true)
    window.addEventListener('pointermove', onPointerMove, true)
    return () => {
      window.removeEventListener('contextmenu', onContextMenu, true)
      window.removeEventListener('pointerdown', onPointerDown, true)
      window.removeEventListener('pointerup', onPointerUp, true)
      window.removeEventListener('pointercancel', onPointerCancel, true)
      window.removeEventListener('pointermove', onPointerMove, true)
    }
  }, [enabled, orbit, press])

  return null
}
